/**
 * RTL / GUC tier spreads — `/correspondents/admin/rtl_tier_spreads`.
 *
 * The account-spread baseline a correspondent prices at: each correspondent picks
 * a tier (Gold / Silver / Bronze, sourced from THIS table — never a constant) on
 * its Pricing tab, may enter a per-product spread that overrides the tier for
 * that product, and may add a signed overlay on top
 * (frontend_documentation/correspondent-admin-and-locks.md, owner 2026-10-08).
 *
 *   buy rate = required base rate + account spread (tier, or product override) + overlay
 *
 * `spreadPct` is an annual-rate PERCENT (0.25 = +0.25% on the coupon) and goes
 * out exactly as typed. It is NOT the DSCR bond-point scale of the tier margins
 * on the Pricing Config tab, and NOT the decimal fraction the base rates store.
 *
 * Mirrors TierMarginsPanel (same grid, editor, history dialog). The seed writes
 * the three tiers at 0 with the note "seed - desk sets values" — no guideline
 * states an account spread — so a 0 carrying that note reads "Not yet priced",
 * never as a free tier.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";

import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import AddRounded from "@mui/icons-material/AddRounded";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRounded from "@mui/icons-material/KeyboardArrowUpRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";
import HistoryOutlined from "@mui/icons-material/HistoryOutlined";
import RefreshRounded from "@mui/icons-material/Refresh";
import { useSnackbar } from "notistack";

import Flx from "../../../components/layout/Flx";
import QuickEditDialog from "../../../components/forms/QuickEditDialog";
import {
  FormCheckList,
  FormCheckRow,
  FormFieldGrid,
  FormMoneyField,
  FormSelectField,
  FormTextField,
} from "../../../components/forms/FormPrimitives";
import formatDollar from "../../../_src_shared/utils/functions/formatDollar";
import {
  adminRtlTierSpreadsApi,
  RTL_TIER_DEFAULT_SPREADS,
  RTL_TIER_SEED_NAMES,
  RTL_TIER_SEED_NOTE,
} from "../../../api/adminPricingConfigApi";
import { FORM, formSx, px } from "../../../theme/formStyles";
import { ActionCell, actionColumn } from "../shared/adminTableActions";
import {
  lastChangeColumn,
  MetaChip,
  newestHistoryEdit,
  PRICING_GRID_COL_DEF,
  PricingGrid,
  PricingSection,
  SectionEmpty,
} from "./pricingUi";
import VersionHistoryDialog from "./VersionHistoryDialog";

// The editable config fields the backend accepts (`_TIER_CONFIG_FIELDS`);
// `active` is edited with its own switch.
const FIELDS = [
  { key: "spreadPct", numeric: true },
  { key: "label" },
  { key: "minLoanAmount", numeric: true },
  { key: "maxLoanAmount", numeric: true },
  { key: "description" },
  { key: "notes" },
];

const tierOf = (row) => row?._id || row?.tier || "";

// A zero spread is only "unset" when it is still the seed's zero; a desk that
// deliberately prices a tier at 0 and rewrites the note gets a plain 0.000%.
export const isUnpricedTier = (row) =>
  Number(row?.spreadPct || 0) === 0 &&
  String(row?.notes || "").trim() === RTL_TIER_SEED_NOTE;

// One click of the spread stepper — an eighth of a point, the increment spreads
// are quoted in. Typing still takes any value.
const SPREAD_STEP = 0.125;

const stepSpread = (raw, dir) => {
  const n = Number(raw);
  const base = raw === "" || raw == null || Number.isNaN(n) ? 0 : n;
  // Round to 3dp so repeated clicks don't accumulate float noise (0.375000001).
  return String(Math.round((base + dir * SPREAD_STEP) * 1000) / 1000);
};

const stepBtnSx = {
  p: 0,
  height: 15,
  width: 20,
  borderRadius: "3px",
  color: FORM.color.ink4,
  "&:hover": { color: FORM.color.ink },
};

const fmtPct = (v) =>
  v == null || v === "" ? "—" : `${Number(v) > 0 ? "+" : ""}${Number(v).toFixed(3)}%`;

export default function RtlTierSpreadsPanel() {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [editing, setEditing] = useState(null); // { tier, isNew, form } | null
  const [historyTier, setHistoryTier] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    adminRtlTierSpreadsApi
      .list()
      .then((r) => setRows(r))
      .catch((err) => {
        setRows([]);
        enqueueSnackbar(
          err?.response?.data?.detail || "Couldn't load RTL tier spreads",
          { variant: "error" },
        );
      })
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  // The section header shows the newest edit across every tier (the history is
  // per tier, so the tier name rides along as the detail).
  const sectionLastEdit = useMemo(() => newestHistoryEdit(rows, tierOf), [rows]);

  const missingSeedTiers = useMemo(() => {
    const have = new Set((rows || []).map(tierOf));
    return RTL_TIER_SEED_NAMES.filter((t) => !have.has(t));
  }, [rows]);

  const defaultsText = (tiers) =>
    tiers.map((t) => `${t} +${RTL_TIER_DEFAULT_SPREADS[t]}%`).join(", ");

  const seed = useCallback(() => {
    setSeeding(true);
    adminRtlTierSpreadsApi
      .seed((rows || []).map(tierOf))
      .then((out) => {
        const created = out?.created || [];
        enqueueSnackbar(
          created.length
            ? `Created ${defaultsText(created)}`
            : "Every tier already exists",
          { variant: created.length ? "success" : "info" },
        );
        load();
      })
      .catch((err) =>
        enqueueSnackbar(err?.response?.data?.detail || "Seed failed", {
          variant: "error",
        }),
      )
      .finally(() => setSeeding(false));
  }, [rows, enqueueSnackbar, load]);

  const openEdit = useCallback((row) => {
    setEditing({
      // A new tier starts on the first missing name; the select offers the rest.
      tier: row ? tierOf(row) : missingSeedTiers[0] || "",
      isNew: !row,
      form: {
        ...FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: row?.[f.key] ?? "" }), {}),
        // A new tier starts on its default spread (Gold 0.125 / Silver 0.25 /
        // Bronze 0.375); the desk can change it before saving.
        ...(row
          ? null
          : { spreadPct: String(RTL_TIER_DEFAULT_SPREADS[missingSeedTiers[0]] ?? "") }),
        active: row ? row.active !== false : true,
      },
    });
  }, [missingSeedTiers]);

  const setField = (key) => (v) =>
    setEditing((prev) => ({ ...prev, form: { ...prev.form, [key]: v } }));

  const save = useCallback(() => {
    if (!editing?.tier?.trim()) return;
    const body = { active: !!editing.form.active };
    FIELDS.forEach((f) => {
      const raw = editing.form[f.key];
      if (raw === "" || raw == null) return;
      body[f.key] = f.numeric ? Number(raw) : raw;
    });
    if (body.spreadPct == null || Number.isNaN(body.spreadPct)) {
      enqueueSnackbar("Account spread is required", { variant: "warning" });
      return;
    }
    adminRtlTierSpreadsApi
      .upsert(editing.tier.trim(), body)
      .then(() => {
        enqueueSnackbar(`${editing.tier} saved`, { variant: "success" });
        setEditing(null);
        load();
      })
      .catch((err) =>
        enqueueSnackbar(err?.response?.data?.detail || "Save failed", {
          variant: "error",
        }),
      );
  }, [editing, enqueueSnackbar, load]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Tier",
        colId: "tier",
        valueGetter: (p) => tierOf(p.data),
        width: 130,
        cellClass: "key",
        sort: "asc",
      },
      { headerName: "Label", field: "label", minWidth: 130, flex: 1 },
      {
        headerName: "Spread (%)",
        field: "spreadPct",
        width: 150,
        cellClass: "num",
        headerClass: "num",
        cellRenderer: (p) =>
          isUnpricedTier(p.data) ? (
            <Flx ac sx={{ height: "100%", justifyContent: "flex-end" }}>
              <Tooltip title="Seeded at 0 — the desk hasn't set this tier's spread yet. Correspondents on it price with no tier spread.">
                <span>
                  <MetaChip tone="warn">Not yet priced</MetaChip>
                </span>
              </Tooltip>
            </Flx>
          ) : (
            fmtPct(p.value)
          ),
      },
      {
        headerName: "Status",
        colId: "active",
        width: 110,
        valueGetter: (p) => (p.data?.active === false ? "Retired" : "Active"),
        cellRenderer: (p) => (
          <Flx ac sx={{ height: "100%" }}>
            {p.value === "Retired" ? (
              <Tooltip title="Kept on file but not offered to correspondents, and it prices nothing.">
                <span>
                  <MetaChip tone="warn">Retired</MetaChip>
                </span>
              </Tooltip>
            ) : (
              <MetaChip>Active</MetaChip>
            )}
          </Flx>
        ),
      },
      {
        headerName: "Loan range",
        colId: "loanRange",
        minWidth: 190,
        cellClass: "num",
        headerClass: "num",
        valueGetter: (p) =>
          p.data?.minLoanAmount == null && p.data?.maxLoanAmount == null
            ? "—"
            : `${formatDollar(p.data?.minLoanAmount, "—")} – ${formatDollar(p.data?.maxLoanAmount, "—")}`,
      },
      lastChangeColumn(),
      actionColumn({
        count: 2,
        cellRenderer: (p) => (
          <ActionCell>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => openEdit(p.data)}>
                <EditOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Version history">
              <IconButton
                size="small"
                onClick={() => setHistoryTier(tierOf(p.data))}
              >
                <HistoryOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </ActionCell>
        ),
      }),
    ],
    [openEdit],
  );

  return (
    <>
      <PricingSection
        id="rtl-tiers"
        lastEdit={sectionLastEdit}
        title={`Tier spreads${rows ? ` (${rows.length})` : ""}`}
        desc="The account spread each correspondent tier adds to the buy rate, in percent of annual rate (0.25 = +0.25%) — not DSCR bond points. A correspondent picks its tier on its Pricing tab; a per-product spread entered there overrides the tier for that product, and its overlay adds on top. This list IS the tier dropdown."
        actions={
          <Flx ac g={1}>
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={load} disabled={loading} size="small">
                  <RefreshRounded fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {rows && missingSeedTiers.length ? (
              <Tooltip
                title={`Creates ${defaultsText(missingSeedTiers)}. Never overwrites an existing tier.`}
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={seed}
                    disabled={seeding}
                  >
                    Seed Gold / Silver / Bronze
                  </Button>
                </span>
              </Tooltip>
            ) : null}
            {/* The tier set is fixed (Gold / Silver / Bronze), so adding only
                exists while one of them is missing. */}
            {rows && missingSeedTiers.length ? (
              <Button
                size="small"
                variant="contained"
                startIcon={<AddRounded />}
                onClick={() => openEdit(null)}
              >
                Add tier
              </Button>
            ) : null}
          </Flx>
        }
        dense
      >
        {rows && rows.length === 0 ? (
          <SectionEmpty>
            No tiers yet. Until one exists, correspondents can't pick an RTL
            tier and price off their per-product spreads only. Seed Gold /
            Silver / Bronze to start.
          </SectionEmpty>
        ) : (
          <PricingGrid rowCount={(rows || []).length} maxRows={10}>
            <AgGridReact
              rowData={rows || []}
              columnDefs={columnDefs}
              defaultColDef={PRICING_GRID_COL_DEF}
              getRowId={(p) => String(tierOf(p.data))}
              tooltipShowDelay={300}
              loading={loading}
            />
          </PricingGrid>
        )}
      </PricingSection>

      {/* Styled as the quick-edit modal (FORM_STYLE_GUIDE.md §12c): 18/600
          title with no header rule, label-above fields, ruled footer, Enter
          saves. Wider than its 460 default because the fields pair up. */}
      <QuickEditDialog
        open={!!editing}
        title={editing?.isNew ? "Add RTL tier" : `Edit ${editing?.tier} tier`}
        description="Account spread in percent of annual rate — not DSCR bond points."
        onClose={() => setEditing(null)}
        onSave={save}
        canSave={
          !!editing?.tier?.trim() &&
          editing?.form?.spreadPct !== "" &&
          editing?.form?.spreadPct != null &&
          !Number.isNaN(Number(editing?.form?.spreadPct))
        }
        hideFieldLabel={false}
        width={560}
        bodySx={{ pt: px(FORM.s.s5) }}
        fieldText="field"
      >
        <FormFieldGrid>
          {/* A select, not free text: the correspondent's rtl_pricing_tier only
              offers Gold / Silver / Bronze (its schema options), so a tier
              named anything else could never be picked. A new tier is one of
              the missing three; an existing one can't be renamed. */}
          <FormSelectField
            span={6}
            label="Tier"
            required
            value={editing?.tier}
            options={editing?.isNew ? missingSeedTiers : [editing?.tier || ""]}
            disabled={!editing?.isNew}
            onChange={(v) =>
              setEditing((prev) => {
                // Switching tier on a new row moves the spread to that tier's
                // default, unless the desk already typed a different value.
                const prevDefault = String(RTL_TIER_DEFAULT_SPREADS[prev.tier] ?? "");
                const untouched =
                  prev.form.spreadPct === "" || prev.form.spreadPct === prevDefault;
                return {
                  ...prev,
                  tier: v || "",
                  form:
                    prev.isNew && untouched
                      ? { ...prev.form, spreadPct: String(RTL_TIER_DEFAULT_SPREADS[v] ?? "") }
                      : prev.form,
                };
              })
            }
            helperText={
              editing?.isNew
                ? "Correspondents pick their RTL tier from Gold, Silver and Bronze."
                : "The tier can't be renamed."
            }
          />
          <FormTextField
            span={6}
            label="Account spread"
            required
            type="number"
            value={editing?.form?.spreadPct}
            onChange={setField("spreadPct")}
            helperText={`Default ${defaultsText(RTL_TIER_SEED_NAMES)}. The arrows step by ${SPREAD_STEP}%.`}
            endAdornment={
              <Flx ac g={0.75} sx={{ flexShrink: 0 }}>
                <Box component="span" sx={formSx.inputSuffix}>
                  %
                </Box>
                <Flx column>
                  <IconButton
                    size="small"
                    aria-label={`Increase spread by ${SPREAD_STEP}%`}
                    onClick={() =>
                      setField("spreadPct")(
                        stepSpread(editing?.form?.spreadPct, 1),
                      )
                    }
                    sx={stepBtnSx}
                  >
                    <KeyboardArrowUpRounded sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label={`Decrease spread by ${SPREAD_STEP}%`}
                    onClick={() =>
                      setField("spreadPct")(
                        stepSpread(editing?.form?.spreadPct, -1),
                      )
                    }
                    sx={stepBtnSx}
                  >
                    <KeyboardArrowDownRounded sx={{ fontSize: 16 }} />
                  </IconButton>
                </Flx>
              </Flx>
            }
          />
          <FormTextField
            span={6}
            label="Label"
            value={editing?.form?.label}
            onChange={setField("label")}
          />
          <FormMoneyField
            span={3}
            label="Min loan amount"
            value={editing?.form?.minLoanAmount}
            onChange={setField("minLoanAmount")}
          />
          <FormMoneyField
            span={3}
            label="Max loan amount"
            value={editing?.form?.maxLoanAmount}
            onChange={setField("maxLoanAmount")}
          />
          <FormTextField
            span={6}
            label="Description"
            value={editing?.form?.description}
            onChange={setField("description")}
          />
          <FormTextField
            span={6}
            label="Notes"
            multiline
            minRows={2}
            value={editing?.form?.notes}
            onChange={setField("notes")}
          />
        </FormFieldGrid>
        <FormCheckList>
          <FormCheckRow
            title="Active"
            subtext="Retire a tier instead of deleting it — it stays on file but isn't offered and prices nothing."
            value={!!editing?.form?.active}
            onChange={setField("active")}
          />
        </FormCheckList>
      </QuickEditDialog>

      <VersionHistoryDialog
        open={!!historyTier}
        title={`RTL tier spread history — ${historyTier}`}
        onClose={() => setHistoryTier(null)}
        fetcher={() => adminRtlTierSpreadsApi.history(historyTier)}
      />
    </>
  );
}
